import React, {ChangeEvent, useRef, useState} from 'react';
import {useData} from "../../hooks/useData";
import {doFetch, FetchResult} from "../../util/fetch";
import {useLocalStorage} from "../../hooks/useLocalStorage";
import {LucideCrown, LucidePlus, LucideSword} from "lucide-react";
import Button from "../../components/Button";
import {useOverlay} from "./overlay/OverlayContext";

export default function ServerMenu(props: {hide: () => void}) {
    const [serverId, setServerId] = useLocalStorage("serverId", false)

    const timestamp = useRef(Date.now());
    const servers = useData<ServersResponse>(`/api/client/servers?t=${timestamp.current}`);

    const [showAddOverlay, setShowAddOverlay] = useState<boolean>(false)

    const overlay = useOverlay()

    function selectServer(newServerId: string){
        const reload = newServerId !== serverId
        setServerId(newServerId)
        if (reload){
            window.location.reload()
        }
        else{
            props.hide()
        }
    }

    return (
        <div className="size-full flex flex-col p-4 relative space-y-4">

            {showAddOverlay && (<AddServerOverlay/>)}

            <div className="flex justify-between items-center m-4">
                <h1 className="text-2xl font-bold">Select a Game Server</h1>

                <button className="px-4 py-2 rounded-md bg-primary hover:opacity-90 flex items-center"
                        onClick={() => setShowAddOverlay(true)}>
                    <LucidePlus size={16}/>
                    <span className="ms-2">Add Server</span>
                </button>
            </div>

            <div className="flex flex-col items-baseline sm:flex-row flex-wrap sm:grid-cols-2 gap-4 m-4">
                {servers.data && servers.data.servers.map((server, index) => (
                    // eslint-disable-next-line eqeqeq
                    <div
                        className={`rounded-lg p-4 transition-shadow min-w-40 w-full sm:w-80 cursor-pointer ${serverId === server.id ? "bg-primary" : "bg-neutral-600 hover:bg-neutral-500"}`}
                        key={index} onClick={() => selectServer(server.id)}>
                        <div className="flex items-baseline justify-between">
                            <h3 className="text-lg font-semibold">{server.name}</h3>
                            <span
                                className={`h-4 w-4 rounded-full m-2 flex-shrink-0 ${server.isDisabled ? "bg-neutral-500" : server.isConnected ? "bg-green-500" : "bg-red-500"}`}></span>
                        </div>
                        <div className="flex mt-6">
                            {server.isOwner ? <LucideCrown size={16} className="flex-shrink-0"/> :
                                <LucideSword size={16} className="flex-shrink-0"/>}
                            <span className="ms-2">{server.isOwner ? "Owner" : "Moderator"}</span>
                        </div>
                    </div>
                ))}
            </div>

        </div>
    );

    function AddServerOverlay() {
        const [tab, setTab] = useState<"plugin" | "vanilla">("plugin");
        const [form, setForm] = useState<FormState>({
            name: "",
            ip: "",
            port: "",
            password: "",
            https: false,
        });
        const [errors, setErrors] = useState<FormErrors>({});

        const [loading, setLoading] = useState<boolean>()

        const [createdToken, setCreatedToken] = useState<string | null>(null);
        const [copied, setCopied] = useState(false);

        const validateVanilla = () => {
            const newErrors: FormErrors = {};
            if (!form.name) newErrors.name = "Name is required.";
            if (!form.ip) newErrors.ip = "IP address is required.";
            else if (!/^\d{1,3}(\.\d{1,3}){3}$/.test(form.ip)) newErrors.ip = "Invalid IP address.";
            if (!form.port) newErrors.port = "Port is required.";
            else if (!/^\d{1,5}$/.test(form.port)) newErrors.port = "Invalid port number.";
            if (!form.password) newErrors.password = "Password is required.";
            setErrors(newErrors);
            return Object.keys(newErrors).length === 0;
        };

        const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
            const { name, value, type, checked } = e.target;
            setForm({ ...form, [name]: type === "checkbox" ? checked : value });
        };

        const handleResponse = (response: FetchResult<AddServerResponse>) => {
            if (response.data && response.data.success) {
                timestamp.current = Date.now()
                setShowAddOverlay(false)
            }
            else {
                const error = response.data?.error ?? response.errorString ?? "Unknown error"
                setErrors({ post: error })
                if (response.error && response.error.code === 67){
                    overlay.openErrorOverlay(response.error)
                }
            }
        };

        const handleAddPlugin = async () => {
            if (!form.name) {
                setErrors({ name: "Name is required." });
                return;
            }

            setLoading(true)
            const createRes = await doFetch<AddServerResponse>("/api/client/servers", {
                body: JSON.stringify({ name: form.name, pluginRcon: true }),
                headers: { "Content-Type": "application/json" },
                method: "POST"
            })

            if (!createRes.data?.success) {
                setLoading(false)
                handleResponse(createRes)
                return
            }

            // Server created — refresh the list (applied when the popup closes) and issue a token.
            timestamp.current = Date.now()
            const tokenRes = await doFetch<{ success: boolean, token?: string }>(`/api/client/servers/settings/pluginrcon/token?serverId=${createRes.data.id}`, {
                body: JSON.stringify({}),
                headers: { "Content-Type": "application/json" },
                method: "POST"
            })
            setLoading(false)

            if (tokenRes.data?.success && tokenRes.data.token) {
                setCreatedToken(tokenRes.data.token)
                setCopied(false)
            } else {
                setErrors({ post: "Server created, but token generation failed. You can generate one from the server settings." })
            }
        };

        const handleCopy = async () => {
            if (!createdToken) {
                return
            }
            try {
                await navigator.clipboard.writeText(createdToken)
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
            } catch {
                // clipboard may be unavailable; the field is selectable as a fallback
            }
        };

        const handleTestConnection = async () => {
            if (!validateVanilla()) {
                return
            }

            setLoading(true)
            const response = await doFetch<AddServerResponse>("/api/client/servers", {
                body: JSON.stringify({
                    name: form.name,
                    ip: form.ip,
                    port: form.port,
                    password: form.password,
                    secure: form.https,
                    pluginRcon: false
                }),
                headers:{
                    "Content-Type": "application/json"
                },
                method: "POST"
            })
            setLoading(false)
            handleResponse(response)
        };

        const tabClass = (active: boolean) =>
            `px-4 py-2 text-sm font-medium -mb-px border-b-2 ${active
                ? "border-primary text-white"
                : "border-transparent text-neutral-400 hover:text-neutral-200"}`;

        return (
            <div className="fixed inset-0 bg-neutral-800 bg-opacity-50 flex justify-center items-center">
            <div className="p-6 max-w-md mx-auto bg-neutral-900 text-white rounded-2xl shadow-md space-y-4 border border-neutral-700">
                <h2 className="text-xl font-bold">Add Server</h2>

                {createdToken ? (
                <div className="space-y-4">
                    <p className="text-sm text-neutral-300">
                        Paste this token into the config file <code className="text-neutral-200">RustServerTool.json</code> under{" "}
                        <code className="text-neutral-200">"Plugin RCON token"</code> and reload the plugin.
                    </p>

                    <div>
                        <label className="block font-medium">Plugin RCON Token</label>
                        <div className="mt-1 flex gap-2">
                            <input
                                readOnly
                                value={createdToken}
                                onFocus={(e) => e.target.select()}
                                className="flex-1 border border-neutral-700 bg-neutral-800 text-white rounded px-3 py-2 font-mono text-sm"
                            />
                            <Button
                                text={copied ? "Copied" : "Copy"}
                                onClick={handleCopy}
                                className="px-4 py-2 rounded-2xl bg-neutral-700 hover:bg-neutral-600"
                            />
                        </div>
                        <p className="text-xs text-amber-400 mt-1">Copy it now — it won't be shown again.</p>
                    </div>

                    <div className="flex justify-end mt-4">
                        <Button
                            text="Done"
                            onClick={() => setShowAddOverlay(false)}
                            className="px-4 py-2 rounded-2xl border border-blue-500 bg-blue-600 text-white shadow hover:bg-blue-700"
                        />
                    </div>
                </div>
                ) : (
                <>
                <div className="flex border-b border-neutral-700">
                    <button className={tabClass(tab === "plugin")} onClick={() => { setTab("plugin"); setErrors({}); }}>
                        Plugin RCON
                    </button>
                    <button className={tabClass(tab === "vanilla")} onClick={() => { setTab("vanilla"); setErrors({}); }}>
                        Vanilla RCON
                    </button>
                </div>

                <div>
                    <label className="block font-medium">Name</label>
                    <input
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        className="mt-1 block w-full border border-neutral-700 bg-neutral-800 text-white rounded px-3 py-2"
                    />
                    {errors.name && <p className="text-red-400 text-sm mt-1">{errors.name}</p>}
                </div>

                {tab === "plugin" ? (
                    <div className="text-sm text-neutral-300 bg-neutral-800 border border-neutral-700 rounded p-3">
                        Plugin RCON requires the <a className="text-primary hover:underline" rel="noreferrer" href="https://codefling.com/plugins/rust-server-tool" target="_blank">RustServerTool plugin</a> installed on your server.
                    </div>
                ) : (
                    <>
                        <div className="flex space-x-2">
                            <div className="flex-1">
                                <label className="block font-medium">IP</label>
                                <input
                                    name="ip"
                                    value={form.ip}
                                    onChange={handleChange}
                                    className="mt-1 block w-full border border-neutral-700 bg-neutral-800 text-white rounded px-3 py-2"
                                />
                                {errors.ip && <p className="text-red-400 text-sm mt-1">{errors.ip}</p>}
                            </div>
                            <div className="w-1/3">
                                <label className="block font-medium">RCON Port</label>
                                <input
                                    name="port"
                                    value={form.port}
                                    onChange={handleChange}
                                    className="mt-1 block w-full border border-neutral-700 bg-neutral-800 text-white rounded px-3 py-2"
                                />
                                {errors.port && <p className="text-red-400 text-sm mt-1">{errors.port}</p>}
                            </div>
                        </div>

                        <div>
                            <label className="block font-medium">Rcon Password</label>
                            <input
                                name="password"
                                type="password"
                                value={form.password}
                                onChange={handleChange}
                                className="mt-1 block w-full border border-neutral-700 bg-neutral-800 text-white rounded px-3 py-2"
                            />
                            {errors.password && <p className="text-red-400 text-sm mt-1">{errors.password}</p>}
                        </div>

                        <div className="flex items-center space-x-2">
                            <input
                                id="https"
                                name="https"
                                type="checkbox"
                                checked={form.https}
                                onChange={handleChange}
                                className="h-5 w-5 text-blue-500 focus:ring-blue-500 border-neutral-700 bg-neutral-800"
                            />
                            <label htmlFor="https" className="font-medium select-none">
                                RCON over HTTPS
                            </label>
                        </div>
                    </>
                )}

                <div className="flex justify-end space-x-4 mt-4">
                    <button
                        onClick={() => setShowAddOverlay(false)}
                        className="px-4 py-2 rounded-2xl border border-neutral-600 text-white shadow hover:bg-neutral-800"
                    >
                        Cancel
                    </button>
                    <Button loading={loading} text={tab === "plugin" ? "Add Server" : "Test connection"}
                        onClick={tab === "plugin" ? handleAddPlugin : handleTestConnection}
                        className="px-4 py-2 rounded-2xl border border-blue-500 bg-blue-600 text-white shadow hover:bg-blue-700"
                    />
                </div>
                {errors.post && <p className="text-red-400 text-sm mt-1">{errors.post}</p>}
                </>
                )}
            </div>
            </div>
        );
    }
};

interface FormState {
    name: string;
    ip: string;
    port: string;
    password: string;
    https: boolean;
}

interface FormErrors {
    name?: string;
    ip?: string;
    port?: string;
    password?: string;
    post?: string
}


interface ServersResponse {
    servers: GameServer[];
}

interface GameServer {
    id: string
    name: string
    isOwner: boolean
    isConnected: boolean
    isDisabled: boolean
}

interface AddServerResponse {
    id: string
    success: boolean
    error: string
}